import { resolveDisplayApplicationState } from '@/lib/pendingProcessing';
import type { ApplicationState } from '@/types';

/** Default order when cycling the Status column (each click = next state on top). */
export const STATUS_PIN_CYCLE: ApplicationState[] = [
  'READY_TO_PROCESS',
  'PIPELINE_RUNNING',
  'PENDING_UPGRADE',
  'PIPELINE_ERROR',
  'APPLIED_NEW',
  'AWAITING_DECISION',
  'OFFER_DECLINED',
  'REJECTED',
  'HIRED',
  'WITHDRAWN',
];

/** Only states that appear in the current list, preserving cycle order. */
export function getPresentStatusPinCycle(
  items: { state?: string }[],
  hasProcessingCredits?: boolean,
): ApplicationState[] {
  const present = new Set(
    items
      .map((a) =>
        a.state
          ? resolveDisplayApplicationState(a.state as ApplicationState, hasProcessingCredits)
          : undefined,
      )
      .filter(Boolean),
  );
  return STATUS_PIN_CYCLE.filter(s => present.has(s));
}

export function getNextStatusPin(
  current: ApplicationState | null,
  cycle: ApplicationState[],
): ApplicationState | null {
  if (cycle.length === 0) return null;
  if (current === null) return cycle[0] ?? null;
  const idx = cycle.indexOf(current);
  if (idx === -1) return cycle[0] ?? null;
  if (idx >= cycle.length - 1) return null;
  return cycle[idx + 1] ?? null;
}

/** Rank for alphabetical / column sort on status (same order as pin cycle). */
export function applicationStateSortRank(state?: string): number {
  if (!state) return STATUS_PIN_CYCLE.length;
  const idx = STATUS_PIN_CYCLE.indexOf(state as ApplicationState);
  return idx === -1 ? STATUS_PIN_CYCLE.length : idx;
}

/** Stable: matching `pinnedState` first, keep relative order within each group. */
export function partitionByPinnedState<T extends { state?: string }>(
  items: T[],
  pinnedState: ApplicationState,
): T[] {
  const matching: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (item.state === pinnedState) matching.push(item);
    else rest.push(item);
  }
  return [...matching, ...rest];
}

/** Stable: listed ids first (e.g. in-flight bulk queue), preserve order within groups. */
export function promoteRowsById<T extends { id: string }>(
  items: T[],
  ids: Set<string>,
): T[] {
  if (ids.size === 0) return items;
  const top: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (ids.has(item.id)) top.push(item);
    else rest.push(item);
  }
  return [...top, ...rest];
}

/**
 * Compose table order: user column sort → optional status pin → optional row promotion.
 */
export function buildApplicationDisplayOrder<T extends { id: string; state?: string }>(
  sorted: T[],
  options?: {
    pinnedStatus?: ApplicationState | null;
    promoteIds?: Set<string>;
    /** Align status pin with badge when PENDING_UPGRADE displays as READY_TO_PROCESS. */
    hasProcessingCredits?: boolean;
  },
): T[] {
  let result = sorted;
  if (options?.pinnedStatus) {
    const pin = options.pinnedStatus;
    const credits = options.hasProcessingCredits;
    const matching: T[] = [];
    const rest: T[] = [];
    for (const item of result) {
      const effective = item.state
        ? resolveDisplayApplicationState(item.state as ApplicationState, credits)
        : undefined;
      if (effective === pin) matching.push(item);
      else rest.push(item);
    }
    result = [...matching, ...rest];
  }
  if (options?.promoteIds?.size) {
    result = promoteRowsById(result, options.promoteIds);
  }
  return result;
}

/** @deprecated Prefer `partitionByPinnedState` on an already-sorted list. */
export function sortApplicationsByPinnedState<T extends { state?: string; updatedAt?: string }>(
  items: T[],
  pinnedState: ApplicationState,
): T[] {
  return partitionByPinnedState(items, pinnedState);
}
